import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Mail, Clock, Trash2, RefreshCw, Plus,
  MessageSquare, Infinity, Calendar, Inbox, Play, TimerReset, Pin, Reply, Search, X, UserPlus, Ticket
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import StatCard from "./StatCard";
import StatusBadge from "./StatusBadge";
import EmailCard from "./EmailCard";

export default function DashboardContent({
  stats, emails, isLoading, expandedEmail, toggleEmailExpand,
  fetchStats, fetchEmails, deleteEmail, deleteEmailsBulk, deleteAllEmails, extendEmail, expireEmail, togglePinEmail, toggleReplyEmail, copyToClipboard,
  formatDate, formatRemainingTime, setIsCreateModalOpen, openEmailInbox, onSwitchToPublic, siteSettings,
  emailSearchQuery, setEmailSearchQuery,
  canDeleteEmails = true,
  users = [], assignEmailToUser, unassignEmailFromUser, fetchEmails2,
  token
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const debounceRef = useRef(null);
  const [ticketStats, setTicketStats] = useState(null);

  const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

  const fetchTicketStats = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/tickets/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketStats(res.data);
    } catch {}
  }, [token, BACKEND_URL]);

  useEffect(() => {
    fetchTicketStats();
    const interval = setInterval(fetchTicketStats, 30000);
    return () => clearInterval(interval);
  }, [fetchTicketStats]);

  // Debounced search
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchEmails(emailSearchQuery);
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // fetchEmails est volontairement exclu : il change à chaque rendu du parent,
    // l'inclure relancerait la recherche en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSearchQuery]);

  const durations = siteSettings?.durations || [];
  const allowPermanent = siteSettings?.allow_permanent !== false;

  const allSelected = emails.length > 0 && selectedIds.length === emails.length;
  const someSelected = selectedIds.length > 0;

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : emails.map(e => e.id));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setIsDeleting(true);
    await deleteEmailsBulk(selectedIds);
    setSelectedIds([]);
    setIsDeleting(false);
  };

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    setShowDeleteAllConfirm(false);
    await deleteAllEmails();
    setSelectedIds([]);
    setIsDeleting(false);
  };

  return (
    <>
      {/* Stats Cards */}
      <div className="pb-2">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-4 mb-6">
          <StatCard icon={Mail} label="Total créés" value={stats?.total_emails || 0} color="text-white" />
          <StatCard icon={Clock} label="Actifs" value={stats?.active_emails || 0} color="text-green-400" />
          <StatCard icon={Infinity} label="Perm." value={stats?.permanent_emails || 0} color="text-amber-400" />
          <StatCard icon={MessageSquare} label="Messages" value={stats?.total_messages || 0} color="text-cyan-400" />
          <StatCard icon={Ticket} label="Tickets" value={ticketStats?.not_closed || 0} color={ticketStats?.not_closed > 0 ? "text-amber-400" : "text-green-400"} />
          <StatCard icon={Calendar} label="Auj." value={stats?.emails_today || 0} color="text-purple-400" />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2.5 min-w-0 flex-shrink-0">
          <h2 className="text-sm sm:text-base font-mono font-semibold text-white tracking-tight">Emails</h2>
          {someSelected && (
            <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 text-[10px] sm:text-xs font-mono whitespace-nowrap">
              {selectedIds.length}
            </Badge>
          )}
        </div>
        <div className="flex gap-1 sm:gap-2 flex-shrink-0">
          {someSelected && canDeleteEmails ? (
            <Button
              onClick={handleBulkDelete}
              disabled={isDeleting}
              size="sm"
              className="bg-red-500 hover:bg-red-600 text-white text-xs"
              data-testid="delete-selected-emails-btn"
            >
              <Trash2 className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 ${isDeleting ? "animate-pulse" : ""}`} />
              Supprimer ({selectedIds.length})
            </Button>
          ) : (
            <>
              {canDeleteEmails && (
              <Button
                onClick={() => setShowDeleteAllConfirm(true)}
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                disabled={emails.length === 0}
                data-testid="delete-all-emails-btn"
              >
                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline sm:ml-1">Tout supprimer</span>
              </Button>
              )}
              <Button
                onClick={() => { fetchStats(); fetchEmails(); }}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-xs hover:text-amber-400 hover:border-amber-500/40 hover:bg-amber-500/10 group/refresh"
                disabled={isLoading}
              >
                <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-500 ${isLoading ? "animate-spin" : "group-hover/refresh:rotate-[360deg]"}`} />
                <span className="hidden sm:inline sm:ml-2">Actualiser</span>
              </Button>
            </>
          )}
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-black text-xs"
          >
            <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="ml-1">Créer</span>
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative group/search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 group-focus-within/search:text-amber-400/60 transition-colors" />
          <Input
            value={emailSearchQuery}
            onChange={(e) => setEmailSearchQuery(e.target.value)}
            placeholder="Rechercher par adresse, expéditeur ou sujet..."
            className="pl-9 pr-8 bg-zinc-900/50 border-zinc-800/60 text-white text-xs h-9 font-mono focus:border-amber-500/30 focus:ring-amber-500/10 transition-colors"
          />
          {emailSearchQuery && (
            <button
              onClick={() => setEmailSearchQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors p-0.5 rounded hover:bg-zinc-800"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Delete All Confirmation */}
      {showDeleteAllConfirm && (
        <div className="mb-4 p-3 sm:p-4 border border-red-500/30 bg-red-500/5 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid="delete-all-confirm">
          <p className="text-red-400 text-xs sm:text-sm font-mono">
            Supprimer tous les emails expirés ?
          </p>
          <div className="flex gap-2 flex-shrink-0">
            <Button onClick={() => setShowDeleteAllConfirm(false)} variant="outline" size="sm" className="border-zinc-700 text-xs">
              Annuler
            </Button>
            <Button onClick={handleDeleteAll} disabled={isDeleting} size="sm" className="bg-red-500 hover:bg-red-600 text-white text-xs" data-testid="confirm-delete-all-btn">
              {isDeleting ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
              Confirmer
            </Button>
          </div>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden md:block border border-zinc-800/50 rounded-xl bg-zinc-900/20 backdrop-blur-sm overflow-hidden">
        <ScrollArea className="h-[400px]">
          <table className="w-full">
            <thead className="bg-zinc-900/80 backdrop-blur-sm sticky top-0 z-10 border-b border-zinc-800/50">
              <tr className="text-left text-[11px] uppercase text-zinc-500 tracking-wider">
                <th className="p-3 w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    className="border-zinc-600 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                    data-testid="select-all-emails-checkbox"
                  />
                </th>
                <th className="p-3">Adresse</th>
                <th className="p-3">Messages</th>
                <th className="p-3">Statut</th>
                <th className="p-3">Durée</th>
                <th className="p-3">Créé le</th>
                <th className="p-3">Restant</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/30">
              {emails.map((email) => (
                <tr key={email.id} className={`hover:bg-zinc-800/20 transition-colors duration-150 ${selectedIds.includes(email.id) ? "bg-cyan-500/5" : ""} ${email.pinned ? "border-l-2 border-l-amber-500" : ""}`}>
                  <td className="p-3">
                    <Checkbox
                      checked={selectedIds.includes(email.id)}
                      onCheckedChange={() => toggleSelect(email.id)}
                      className="border-zinc-600 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                      data-testid={`select-email-${email.id}`}
                    />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {email.pinned && <Pin className="w-3 h-3 text-amber-400 fill-current flex-shrink-0" />}
                      <span
                        onClick={() => copyToClipboard(email.full_address)}
                        className="font-mono text-xs text-white truncate max-w-[200px] cursor-pointer hover:text-cyan-400 transition-colors"
                        title="Cliquer pour copier"
                        data-testid={`copy-email-${email.id}`}
                      >
                        {email.full_address}
                      </span>
                      {email.owner && (
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-medium shrink-0 ${
                            email.owner.role === "super_admin"
                              ? "bg-red-500/10 text-red-400 border border-red-500/20"
                              : email.owner.role === "admin"
                              ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                              : "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
                          }`}
                          title={`Assigné à ${email.owner.username}`}
                        >
                          {email.owner.role === "super_admin" ? "Super Admin" : email.owner.role === "admin" ? "Admin" : "Utilisateur"}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="font-mono text-xs text-cyan-400 font-medium">{email.message_count ?? 0}</span>
                  </td>
                  <td className="p-3">
                    <StatusBadge status={email.status} />
                  </td>
                  <td className="p-3">
                    <span className="text-zinc-400 text-xs font-mono">
                      {email.lifetime === "expired" ? "Expiré" : email.lifetime === "permanent" ? "Permanent" : email.lifetime || "N/A"}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="text-zinc-400 text-xs">{formatDate(email.created_at)}</span>
                  </td>
                  <td className="p-3">
                    <span className={`font-mono text-xs ${
                      email.remaining_seconds === -1 ? "text-amber-400" :
                      email.remaining_seconds <= 0 ? "text-red-400" : "text-green-400"
                    }`}>
                      {formatRemainingTime(email.remaining_seconds)}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <Button
                        onClick={() => togglePinEmail(email.id, email.pinned)}
                        variant="ghost"
                        size="sm"
                        className={`h-8 w-8 p-0 ${email.pinned ? "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" : "text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10"}`}
                        title={email.pinned ? "Désépingler" : "Épingler"}
                        data-testid={`pin-email-${email.id}`}
                      >
                        <Pin className={`w-4 h-4 ${email.pinned ? "fill-current" : ""}`} />
                      </Button>
                      <Button
                        onClick={() => toggleReplyEmail(email.id, email.reply_enabled)}
                        variant="ghost"
                        size="sm"
                        className={`h-8 w-8 p-0 ${email.reply_enabled ? "text-blue-400 hover:text-blue-300 hover:bg-blue-500/10" : "text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10"}`}
                        title={email.reply_enabled ? "Désactiver la réponse" : "Activer la réponse"}
                      >
                        <Reply className={`w-4 h-4 ${email.reply_enabled ? "fill-current" : ""}`} />
                      </Button>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${email.owner ? "text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10" : "text-zinc-500 hover:text-cyan-400 hover:bg-cyan-500/10"}`}
                            title={email.owner ? `Assigné à ${email.owner.username}` : "Assigner à un utilisateur"}
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="bg-zinc-900 border-zinc-700 p-1 w-auto min-w-[160px]" align="end">
                          <div className="flex flex-col max-h-48 overflow-y-auto">
                            <p className="text-[10px] text-zinc-500 uppercase px-3 py-1 font-medium">Assigner à</p>
                            {users.map(u => {
                              const isAssigned = email.owner?.username === u.username;
                              return (
                                <button
                                  key={u.id}
                                  onClick={async () => {
                                    if (isAssigned) {
                                      await unassignEmailFromUser(u.id, email.id);
                                    } else {
                                      await assignEmailToUser(u.id, email.id);
                                    }
                                    fetchEmails2(emailSearchQuery);
                                  }}
                                  className={`text-left text-xs font-mono px-3 py-1.5 rounded transition-colors flex items-center gap-2 ${
                                    isAssigned
                                      ? "text-cyan-400 bg-cyan-500/10"
                                      : "text-zinc-300 hover:bg-cyan-500/10 hover:text-cyan-400"
                                  }`}
                                >
                                  <span className="truncate">{u.username}</span>
                                  {isAssigned && <span className="text-[9px] text-cyan-500/60 shrink-0">assigné</span>}
                                </button>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Button
                        onClick={() => openEmailInbox(email)}
                        variant="ghost"
                        size="sm"
                        className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 h-8 w-8 p-0"
                        title="Voir la boîte de réception"
                      >
                        <Inbox className="w-4 h-4" />
                      </Button>
                      {email.status !== "expired" && (
                        <Button
                          onClick={() => onSwitchToPublic(email)}
                          variant="ghost"
                          size="sm"
                          className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 h-8 w-8 p-0"
                          title="Utiliser dans l'app publique"
                          data-testid={`use-email-${email.id}`}
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      )}
                      {email.status !== "permanent" && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10" title="Prolonger">
                              <TimerReset className="w-4 h-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="bg-zinc-900 border-zinc-700 p-1 w-auto min-w-[140px]" align="end">
                            <div className="flex flex-col">
                              {durations.map(d => (
                                <button key={d.key} onClick={() => extendEmail(email.id, d.key)}
                                  className="text-left text-xs font-mono px-3 py-1.5 text-zinc-300 hover:bg-green-500/10 hover:text-green-400 rounded transition-colors">
                                  +{d.label}
                                </button>
                              ))}
                              <button onClick={() => extendEmail(email.id, "permanent")}
                                className="text-left text-xs font-mono px-3 py-1.5 text-amber-400 hover:bg-amber-500/10 rounded transition-colors">
                                Permanent
                              </button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                      {email.status !== "expired" && (
                        <Button
                          onClick={() => expireEmail(email.id)}
                          variant="ghost"
                          size="sm"
                          className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 h-8 w-8 p-0"
                          title="Faire expirer"
                        >
                          <Clock className="w-4 h-4" />
                        </Button>
                      )}
                      {canDeleteEmails && (
                      <Button
                        onClick={() => deleteEmail(email.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-2">
        {emails.length > 0 && (
          <div className="flex items-center justify-between p-2 border border-zinc-800 rounded-lg bg-zinc-900/50">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleSelectAll}
                className="border-zinc-600 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
              />
              <span className="text-xs text-zinc-400 font-mono">
                {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
              </span>
            </div>
            {someSelected && canDeleteEmails && (
              <Button onClick={handleBulkDelete} disabled={isDeleting} size="sm" className="bg-red-500 hover:bg-red-600 text-white text-xs h-7">
                <Trash2 className="w-3 h-3 mr-1" />
                ({selectedIds.length})
              </Button>
            )}
          </div>
        )}
        <ScrollArea className="h-[calc(100vh-420px)]">
          <div className="space-y-2 pr-2">
            {emails.map((email) => (
              <EmailCard
                key={email.id}
                email={email}
                expanded={expandedEmail === email.id}
                selected={selectedIds.includes(email.id)}
                onToggleSelect={() => toggleSelect(email.id)}
                onToggle={() => toggleEmailExpand(email.id)}
                onCopy={() => copyToClipboard(email.full_address)}
                onDelete={canDeleteEmails ? () => deleteEmail(email.id) : undefined}
                onExtend={(extendBy) => extendEmail(email.id, extendBy)}
                onExpire={() => expireEmail(email.id)}
                onView={() => openEmailInbox(email)}
                onUse={() => onSwitchToPublic(email)}
                onTogglePin={() => togglePinEmail(email.id, email.pinned)}
                onToggleReply={() => toggleReplyEmail(email.id, email.reply_enabled)}
                replyEnabled={email.reply_enabled}
                formatDate={formatDate}
                formatRemainingTime={formatRemainingTime}
                durations={durations}
                users={users}
                assignEmailToUser={assignEmailToUser}
                unassignEmailFromUser={unassignEmailFromUser}
                onRefreshEmails={() => fetchEmails2(emailSearchQuery)}
              />
            ))}
          </div>
        </ScrollArea>
      </div>

      {emails.length === 0 && (
        <div className="text-center py-16 text-zinc-500">
          <div className="w-16 h-16 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-7 h-7 opacity-40" />
          </div>
          <p className="font-mono text-sm">Aucun email</p>
        </div>
      )}
    </>
  );
}
